import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import supabase from '../../../supabase/supabase';

const { FiShield, FiUser, FiArrowRight, FiCheck, FiInfo, FiExternalLink, FiStar, FiChevronRight, FiX, FiCalendar, FiAlertCircle, FiSearch, FiFilter, FiMapPin, FiTrendingUp } = FiIcons;

const InvestigatorSelector = ({ report, onCancel }) => {
  const navigate = useNavigate();
  const { utredere, updateReport } = useData();
  const [selectedType, setSelectedType] = useState(null); // 'internal' or 'external'
  const [availabilityMap, setAvailabilityMap] = useState({});
  const [loadingAvail, setLoadingAvail] = useState(false);
  
  // Feature: Search and Filter State
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCompetence, setFilterCompetence] = useState('all');

  // Finn uavhengige utredere som er aktive
  const externalPros = Array.isArray(utredere) ? utredere.filter(u => u.active) : [];

  useEffect(() => {
    if (selectedType === 'external') {
      fetchAvailability();
    }
  }, [selectedType]);

  const fetchAvailability = async () => {
    try {
      setLoadingAvail(true);
      const today = new Date().toISOString().split('T')[0];
      const { data, error } = await supabase
        .from('utreder_availability_1788615000000')
        .select('*')
        .lte('start_date', today)
        .gte('end_date', today);

      if (error) throw error;

      const map = {};
      data?.forEach(slot => {
        map[slot.utreder_id] = slot;
      });
      setAvailabilityMap(map);
    } catch (err) {
      console.error('Error fetching availability:', err);
    } finally {
      setLoadingAvail(false);
    }
  };

  // Feature: Matching Logic
  const calculateMatchScore = (inv) => {
    let score = 0;
    const reportCategory = report?.category?.toLowerCase() || '';
    const invCompetencies = inv.competence_tags || [];
    if (invCompetencies.some(c => c.toLowerCase().includes(reportCategory))) score += 50;
    else if (invCompetencies.length > 0) score += 20;

    const reportLocation = report?.location?.toLowerCase() || '';
    const invGeography = inv.geography || '';
    if (invGeography.toLowerCase().includes(reportLocation) || invGeography === 'Hele Norge') score += 30;

    const currentLoad = inv.current_load_count || 0;
    const maxCap = inv.max_capacity || 5;
    if (currentLoad < maxCap) score += 20;

    return Math.min(score, 100);
  };

  const handleSelectInternal = () => {
    navigate(`/company/investigation/form/${report.id}`);
  };

  const handleAssignExternal = async (utreder) => {
    if (availabilityMap[utreder.id]) {
      alert(`Denne utrederen er merket som utilgjengelig frem til ${new Date(availabilityMap[utreder.id].end_date).toLocaleDateString('no-NO')}. Vennligst velg en annen utreder.`);
      return;
    }

    const isHMS = utreder.id === 'hms-default';
    const confirmMsg = isHMS 
      ? "Vil du tildele denne saken til HMSVarsling for ekstern håndtering (6 900,-)?"
      : `Vil du tildele saken til ${utreder.fullName || utreder.full_name}? HMSVarsling vil motta en formidlingsprosent av honoraret.`;

    if (window.confirm(confirmMsg)) {
      await updateReport(report.id, {
        assignedInvestigatorId: utreder.id,
        externalHandling: true,
        status: 'assigned',
        history: [...(report.history || []), {
          date: new Date().toISOString(),
          action: `Sak tildelt ekstern utreder: ${isHMS ? 'HMSVarsling' : (utreder.fullName || utreder.full_name)}`,
          user: "System"
        }]
      });
      alert("Saken er tildelt. Utrederen har nå fått tilgang til dokumentene.");
      onCancel();
    }
  };

  // Feature: Filtering and Sorting
  const allCompetencies = Array.from(new Set(externalPros.flatMap(i => i.competence_tags || [])));
  
  const processedExternalPros = externalPros
    .map(inv => ({ ...inv, matchScore: calculateMatchScore(inv) }))
    .filter(inv => {
      const name = (inv.fullName || inv.full_name || '').toLowerCase();
      const company = (inv.companyName || inv.company_name || '').toLowerCase();
      const matchesSearch = name.includes(searchQuery.toLowerCase()) || company.includes(searchQuery.toLowerCase());
      const matchesFilter = filterCompetence === 'all' || inv.competence_tags?.some(c => c.toLowerCase() === filterCompetence.toLowerCase());
      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => b.matchScore - a.matchScore);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-md">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white dark:bg-gray-900 w-full max-w-4xl rounded-[32px] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
      >
        <div className="p-8 border-b dark:border-gray-800 flex justify-between items-center bg-gray-50 dark:bg-gray-800/50">
          <div>
            <h2 className="text-2xl font-black text-gray-900 dark:text-white uppercase tracking-tight">Hvem skal utrede saken?</h2>
            <p className="text-sm text-gray-500">Sikre habilitet ved å velge riktig kompetanse.</p>
          </div>
          <button onClick={onCancel} className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors">
            <SafeIcon icon={FiX} size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-8">
          {!selectedType ? (
            <div className="grid md:grid-cols-2 gap-8">
              {/* INTERN */}
              <motion.button 
                whileHover={{ y: -5 }}
                onClick={() => setSelectedType('internal')}
                className="p-8 rounded-[24px] border-2 border-gray-100 dark:border-gray-700 text-left hover:border-blue-500 transition-all bg-white dark:bg-gray-800 shadow-sm group"
              >
                <div className="w-14 h-14 bg-blue-100 dark:bg-blue-900/30 text-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <SafeIcon icon={FiUser} size={32} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Intern utredning</h3>
                <p className="text-sm text-gray-500 mb-6 leading-relaxed">Bruk bedriftens egne ressurser (HR, Leder, Verneombud). Systemet guider dere gjennom lovpålagte steg.</p>
                <div className="flex items-center gap-2 text-blue-600 font-bold text-sm uppercase tracking-wider">
                  Fortsett internt <SafeIcon icon={FiArrowRight} />
                </div>
              </motion.button>

              {/* EKSTERN */}
              <motion.button 
                whileHover={{ y: -5 }}
                onClick={() => setSelectedType('external')}
                className="p-8 rounded-[24px] border-2 border-gray-100 dark:border-gray-700 text-left hover:border-indigo-500 transition-all bg-white dark:bg-gray-800 shadow-sm group"
              >
                <div className="w-14 h-14 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <SafeIcon icon={FiShield} size={32} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Uavhengig utreder</h3>
                <p className="text-sm text-gray-500 mb-6 leading-relaxed">La en nøytral tredjepart håndtere prosessen for å sikre maksimal habilitet, trygghet og etterlevelse.</p>
                <div className="flex items-center gap-2 text-indigo-600 font-bold text-sm uppercase tracking-wider">
                  Se våre partnere <SafeIcon icon={FiArrowRight} />
                </div>
              </motion.button>
            </div>
          ) : selectedType === 'internal' ? (
            <div className="max-w-md mx-auto text-center space-y-6 py-10">
              <div className="w-20 h-20 bg-blue-50 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto text-blue-600">
                <SafeIcon icon={FiUser} size={40} />
              </div>
              <h3 className="text-2xl font-bold">Start intern prosess</h3>
              <p className="text-gray-500">Du vil nå bli tatt til vårt strukturerte utredningsskjema som guider deg gjennom de 10 stegene i prosessen.</p>
              <div className="flex gap-4 pt-4">
                <button onClick={() => setSelectedType(null)} className="flex-1 py-3 font-bold text-gray-500">Tilbake</button>
                <button onClick={handleSelectInternal} className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-bold shadow-lg">Start nå</button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-black text-gray-900 dark:text-white uppercase tracking-[0.2em] text-xs">Våre utredningspartnere</h3>
                <button onClick={() => setSelectedType(null)} className="text-xs font-bold text-gray-400 hover:text-gray-600 uppercase">Tilbake</button>
              </div>

              {/* Feature: Search and Filter UI */}
              <div className="flex flex-wrap gap-3 mb-6">
                <div className="relative flex-1 min-w-[200px]">
                  <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input 
                    type="text" 
                    placeholder="Søk på navn eller firma..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 rounded-xl bg-gray-50 dark:bg-gray-800 border-2 border-transparent focus:border-indigo-500 outline-none text-sm transition-all"
                  />
                </div>
                <select 
                  value={filterCompetence}
                  onChange={(e) => setFilterCompetence(e.target.value)}
                  className="px-4 py-2 rounded-xl bg-gray-50 dark:bg-gray-800 border-2 border-transparent focus:border-indigo-500 outline-none text-xs font-bold uppercase tracking-wider"
                >
                  <option value="all">Alle fagområder</option>
                  {allCompetencies.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-4">
                {/* DEFAULT: HMSVARSLING - ALLTID ØVERST HVIS INGEN SØK */}
                {(!searchQuery && filterCompetence === 'all') && (
                  <div className={`p-6 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-[24px] text-white shadow-xl flex items-center justify-between group transition-all ${availabilityMap['hms-default'] ? 'opacity-60 grayscale' : 'cursor-pointer hover:scale-[1.01]'}`}
                    onClick={() => handleAssignExternal({id: 'hms-default', fullName: 'HMSVarsling'})}
                  >
                    <div className="flex items-center gap-5">
                      <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-md">
                        <SafeIcon icon={FiShield} size={28} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-black text-xl">HMSVarsling Standard</h4>
                          <span className="bg-white/20 px-2 py-0.5 rounded text-[10px] font-bold uppercase">Anbefalt</span>
                        </div>
                        <p className="text-xs text-blue-100 mt-1">Vårt standardvalg for rask, nøytral og profesjonell saksgang.</p>
                        {availabilityMap['hms-default'] && (
                          <div className="mt-2 flex items-center gap-1.5 text-[10px] font-bold bg-white/10 w-fit px-2 py-0.5 rounded uppercase">
                            <SafeIcon icon={FiCalendar} />
                            Fullbooket til {new Date(availabilityMap['hms-default'].end_date).toLocaleDateString('no-NO')}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex items-center gap-6">
                      <div className="hidden sm:block">
                        <p className="text-[10px] font-black uppercase opacity-60">Fastpris</p>
                        <p className="font-black text-xl">6 900 kr</p>
                      </div>
                      <SafeIcon icon={FiChevronRight} size={24} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-3">
                  {processedExternalPros.filter(p => p.id !== 'hms-default').map(pro => {
                    const isBusy = availabilityMap[pro.id];
                    return (
                      <div key={pro.id} className={`p-5 bg-white dark:bg-gray-800 border-2 rounded-2xl flex items-center justify-between transition-all shadow-sm group ${isBusy ? 'border-gray-100 dark:border-gray-800 opacity-60' : 'border-gray-100 dark:border-gray-700 hover:border-indigo-500 cursor-pointer'}`}
                        onClick={() => handleAssignExternal(pro)}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center text-gray-400 overflow-hidden border border-gray-200">
                            {pro.branding?.logoUrl ? <img src={pro.branding.logoUrl} className="w-full h-full object-cover" /> : <span className="font-black">{(pro.fullName || pro.full_name)?.charAt(0)}</span>}
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                              {pro.fullName || pro.full_name} 
                              {pro.subscription_plan === 'pro' && <SafeIcon icon={FiStar} className="text-yellow-500 text-xs" />}
                              <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${pro.matchScore >= 80 ? 'bg-green-50 text-green-600 border-green-200' : 'bg-blue-50 text-blue-600 border-blue-200'}`}>
                                {pro.matchScore}% Match
                              </span>
                            </h4>
                            <div className="flex flex-wrap gap-2 mt-1">
                              <p className="text-xs text-gray-500 font-medium">{pro.title_role || 'Uavhengig Utreder'}</p>
                              {pro.qualifications?.slice(0, 2).map((q, idx) => (
                                <span key={idx} className="text-[8px] font-black uppercase text-indigo-500 bg-indigo-50 px-1.5 py-0.5 rounded">
                                  {q.label}
                                </span>
                              ))}
                            </div>
                            <div className="flex items-center gap-3 mt-2">
                              {isBusy ? (
                                <div className="flex items-center gap-1.5 text-[9px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded uppercase w-fit">
                                  <SafeIcon icon={FiCalendar} />
                                  Opptatt til {new Date(isBusy.end_date).toLocaleDateString('no-NO')}
                                </div>
                              ) : (
                                <div className="flex items-center gap-1.5 text-[9px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded uppercase w-fit">
                                  <SafeIcon icon={FiTrendingUp} />
                                  Kapasitet: {pro.current_load_count || 0}/{pro.max_capacity || 5}
                                </div>
                              )}
                              <div className="flex items-center gap-1 text-[9px] font-bold text-gray-400 uppercase">
                                <SafeIcon icon={FiMapPin} />
                                {pro.geography}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <button 
                            onClick={(e) => { e.stopPropagation(); navigate(`/u/${pro.slug}`); }}
                            className="p-3 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all"
                            title="Se Microsite"
                          >
                            <SafeIcon icon={FiExternalLink} size={20} />
                          </button>
                          <SafeIcon icon={FiChevronRight} className="text-gray-300 group-hover:text-indigo-600 transition-all" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default InvestigatorSelector;